<template>
  <div class="admin-page">
    <div class="page-header">
      <h1 class="page-title">{{ t('adminIndex.pageTitle') }}</h1>
      <p class="page-subtitle">
        {{ t('adminIndex.pageSubtitle') }}
      </p>
    </div>

    <div v-if="!isAdmin" class="forbidden-state">
      <v-icon size="64" color="error">mdi-shield-alert</v-icon>
      <h2>{{ t('adminIndex.noAccessTitle') }}</h2>
      <p>{{ t('adminIndex.noAccessDescription') }}</p>
    </div>

    <div v-else>
      <div class="admin-quick-actions"></div>

      <v-card class="filter-card" elevation="1">
        <v-card-text>
          <div class="filter-header">
            <div class="filter-title">
              <v-icon size="20" color="#EA5545" class="mr-2">mdi-filter-variant</v-icon>
              <span>{{ t('adminIndex.filters.title') }}</span>
            </div>
          </div>
          <div class="filter-buttons">
            <v-row dense>
              <v-col cols="12" sm="4">
                <v-btn
                  block
                  variant="flat"
                  :class="['filter-btn', { 'filter-btn--active': currentFilter === 'unanswered' }]"
                  @click="currentFilter = 'unanswered'"
                >
                  <v-icon
                    left
                    size="18"
                    class="filter-btn-icon"
                  >
                    mdi-help-circle
                  </v-icon>
                  <span class="filter-btn-label">
                    {{ t('adminIndex.filters.unanswered') }}
                    <span class="filter-btn-count">({{ filterCounts.unanswered || 0 }})</span>
                  </span>
                </v-btn>
              </v-col>
              <v-col cols="12" sm="4">
                <v-btn
                  block
                  variant="flat"
                  :class="['filter-btn', { 'filter-btn--active': currentFilter === 'unread' }]"
                  @click="currentFilter = 'unread'"
                >
                  <v-icon
                    left
                    size="18"
                    class="filter-btn-icon"
                  >
                    mdi-email-alert
                  </v-icon>
                  <span class="filter-btn-label">
                    {{ t('adminIndex.filters.unread') }}
                    <span class="filter-btn-count">({{ filterCounts.unread || 0 }})</span>
                  </span>
                </v-btn>
              </v-col>
              <v-col cols="12" sm="4">
                <v-btn
                  block
                  variant="flat"
                  :class="['filter-btn', { 'filter-btn--active': currentFilter === 'all' }]"
                  @click="currentFilter = 'all'"
                >
                  <v-icon
                    left
                    size="18"
                    class="filter-btn-icon"
                  >
                    mdi-format-list-bulleted
                  </v-icon>
                  <span class="filter-btn-label">
                    {{ t('adminIndex.filters.allTickets') }}
                    <span class="filter-btn-count">({{ filterCounts.all || 0 }})</span>
                  </span>
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </v-card-text>
      </v-card>

      <div v-if="isLoading" class="state-block" data-state="loading">
        <v-progress-circular
          indeterminate
          color="#EA5545"
          size="64"
        ></v-progress-circular>
        <p class="state-text">{{ t('adminIndex.loading') }}</p>
      </div>

      <div
        v-else-if="supports.length > 0"
        class="tickets-list"
      >
        <v-card
          v-for="ticket in supports"
          :key="ticket.sup_id"
          class="ticket-card"
          elevation="1"
          @click="openSupport(ticket.sup_id)"
        >
          <v-card-text class="ticket-card-content">
            <div class="ticket-main">
              <div class="ticket-header-row">
                <span class="ticket-id">#{{ ticket.sup_id }}</span>
                <v-chip
                  :color="getStatusColor(ticket.sup_status)"
                  size="small"
                  class="status-chip"
                >
                  {{ getStatusText(ticket.sup_status) }}
                </v-chip>
              </div>
              <h3 class="ticket-title">{{ ticket.sup_title }}</h3>
              <div class="ticket-meta">
                <router-link
                  class="ticket-user-link"
                  :to="{ name: 'adminuserdetail', params: { id: ticket.sup_user_id } }"
                  @click.stop
                >
                  <v-icon size="16" color="#666" class="mr-1">mdi-account</v-icon>
                  {{ ticket.user_name }} (ID {{ ticket.sup_user_id }})
                  <v-icon
                    size="14"
                    class="ml-1"
                    color="#EA5545"
                  >
                    mdi-arrow-top-right
                  </v-icon>
                </router-link>
                <span
                  v-if="ticket.prod_name"
                  class="ticket-product"
                >
                  <v-icon size="16" color="#EA5545" class="mr-1">mdi-package</v-icon>
                  {{ ticket.prod_name }}
                </span>
              </div>
              <p class="ticket-preview">
                {{ truncateText(ticket.sup_text, 160) }}
              </p>
            </div>

            <div class="ticket-side">
              <div class="info-line">
                <span class="info-label">{{ t('adminIndex.labels.created') }}</span>
                <span class="info-value">{{ formatDate(ticket.sup_created_date) }}</span>
              </div>

              <div class="info-line">
                <span class="info-label">{{ t('adminIndex.labels.adminReplies') }}</span>
                <span class="info-value">
                  <v-icon
                    v-if="ticket.admin_reply_count > 0"
                    size="16"
                    color="#4caf50"
                    class="mr-1"
                  >
                    mdi-reply-all
                  </v-icon>
                  {{ ticket.admin_reply_count }}
                </span>
              </div>

              <div
                class="info-line"
                v-if="ticket.unread_count_admin > 0"
              >
                <span class="info-label">{{ t('adminIndex.labels.newReplies') }}</span>
                <span class="info-value unread">
                  <v-icon size="16" color="#EA5545" class="mr-1">mdi-email-alert</v-icon>
                  {{ ticket.unread_count_admin }}
                </span>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <div class="pagination-wrapper">
          <v-pagination
            v-model="pagination.page"
            :length="pagination.total_pages"
            :total-visible="7"
            @update:modelValue="handlePageChange"
          ></v-pagination>
          <div class="pagination-info" v-if="pagination.total > 0">
            {{ t('adminIndex.pagination.pageLabel') }} {{ pagination.page }}
            {{ t('adminIndex.pagination.of') }} {{ pagination.total_pages }}
            · {{ pagination.total }} {{ t('adminIndex.pagination.totalTickets') }}
          </div>
        </div>
      </div>

      <div
        v-else
        class="state-block" data-state="empty"
      >
        <v-icon size="80" color="#ccc">mdi-ticket-outline</v-icon>
        <h3 class="state-title">{{ t('adminIndex.empty.title') }}</h3>
        <p class="state-text">
          {{ t('adminIndex.empty.description') }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { inject, ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useLanguage } from '@/composables/useLanguage'
import { CLEARMENT_TITLE, CLEARMENT_DESCRIPTION } from '@/constants/documentHead'

const baseCalls = inject('baseCalls')
const errorStore = inject('errorStore')
const userStore = inject('userStore')
const { t } = useLanguage()
const updateDocumentHead = inject('updateDocumentHead')
updateDocumentHead?.(CLEARMENT_TITLE, CLEARMENT_DESCRIPTION)

const router = useRouter()

const supports = ref([])
const isLoading = ref(false)
const currentFilter = ref('unanswered')
const pagination = ref({
  page: 1,
  per_page: 10,
  total: 0,
  total_pages: 0
})
const defaultFilterCounts = {
  all: 0,
  unread: 0,
  unanswered: 0
}
const filterCounts = ref({ ...defaultFilterCounts })

const userData = computed(() => userStore?.userData || null)

const isAdmin = computed(() => {
  const u = userData.value
  if (!u || !u.user_admin) return false
  return Number(u.user_admin) === 1
})

function truncateText(text, maxLength) {
  if (!text) return ''
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

function formatDate(dateString) {
  if (!dateString || dateString === '1973-01-01 00:00:00') return t('common.labels.notAvailable')
  const date = new Date(dateString)
  return date.toLocaleDateString('de-DE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function getStatusColor(status) {
  const colors = {
    0: 'orange',
    1: 'green',
    2: 'grey',
    3: 'red'
  }
  return colors[status] || 'grey'
}

function getStatusText(status) {
  const texts = {
    0: 'adminIndex.status.open',
    1: 'adminIndex.status.done',
    2: 'adminIndex.status.closed',
    3: 'adminIndex.status.urgent'
  }
  const key = texts[status] || 'adminIndex.status.unknown'
  return t(key)
}

async function loadAdminSupports() {
  if (!isAdmin.value) {
    supports.value = []
    filterCounts.value = { ...defaultFilterCounts }
    return
  }

  isLoading.value = true

  try {
    const response = await baseCalls.clearmentPost({
      action: 'getadminsupports',
      filter: currentFilter.value,
      page: pagination.value.page,
      per_page: pagination.value.per_page
    })

    if (response.data.success) {
      supports.value = response.data.supports || []
      pagination.value = {
        page: response.data.pagination?.page || 1,
        per_page: response.data.pagination?.per_page || 10,
        total: response.data.pagination?.total || 0,
        total_pages: response.data.pagination?.total_pages || 0
      }
      const counts = response.data.filter_counts || {}
      filterCounts.value = {
        all: Number(counts.all ?? 0),
        unread: Number(counts.unread ?? 0),
        unanswered: Number(counts.unanswered ?? 0)
      }
    } else {
      supports.value = []
      filterCounts.value = { ...defaultFilterCounts }
      errorStore?.setError?.(
        true,
        response.data.message || t('adminIndex.errors.fetchFailed')
      )
    }
  } catch (err) {
    console.error('Error loading admin supports:', err)
    supports.value = []
    filterCounts.value = { ...defaultFilterCounts }
    errorStore?.setError?.(
      true,
      t('adminIndex.errors.fetchFailedGeneric')
    )
  } finally {
    isLoading.value = false
  }
}

function handlePageChange(newPage) {
  pagination.value.page = newPage
  loadAdminSupports()
}

function openSupport(supId) {
  router.push({ name: 'adminsupport', params: { id: supId } })
}

onMounted(() => {
  loadAdminSupports()
})

watch(currentFilter, () => {
  pagination.value.page = 1
  loadAdminSupports()
})
</script>

<style scoped src="@/assets/styles/sfc/components/sites/AdminIndex.css"></style>
